import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { object, string, boolean } from 'yup';
import { Row, Col } from 'react-bootstrap';
import { Alert, Button } from '@storable/canaveral-components';
import get from 'lodash/get';
import withForm from '../../hocs/withForm';
import { FLIPPER_FAVICON } from '../../../constants/flippers';
import { MANAGE } from '../../../modules/authorization/actions';
import { BUILDER } from '../../../modules/authorization/subjects';
import { validateHexCode } from '../../../modules/theme/colors';
import useFlipper from '../../../modules/react/hooks/useFlipper';
import useCurrentUser from '../../hooks/useCurrentUser';
import formatInitialValues from '../../lib/formatInitialValues';
import FormFieldFavicon from '../../containers/FormFieldFavicon';
import FormFieldColorPicker from '../FormFieldColorPicker';
import FormFieldText from '../FormFieldText';
import FormSubmitButton from '../FormSubmitButton';
import FormFieldToggle from '../FormFieldToggle';
import FormFieldMediumUploadRow from '../../containers/FormFieldMediumUploadRow';
import styles from './styles.scss';
import SocialMediaIconForm from '../SocialMediaIconForm';
import buttonStylesJson from './ButtonStylesJson';
import Select from '../Select';

// Import our new custom radius component
import CustomRadiusInput from '../CustomRadiusInput';

// Extracted Constants
const SITE_BRANDING_SECTION_NAME = 'site';

const availableFonts = {
  Montserrat: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap',
  Roboto: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap',
  'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap',
  Lato: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
  Inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap',
};

const textTransformOptions = [
  { id: 'uppercase', label: 'UPPERCASE', style: { textTransform: 'uppercase' } },
  { id: 'lowercase', label: 'lowercase', style: { textTransform: 'lowercase' } },
  { id: 'capitalize', label: 'Title Case', style: { textTransform: 'capitalize' } },
];

// Helper function for capitalizing strings
const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

// PropTypes Helpers for cleaner definitions
const SocialMediaPlatformShape = PropTypes.shape({
  socialMediaPlatform: PropTypes.shape({
    provider: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    link: PropTypes.string,
  }),
});

const ThemeShape = PropTypes.shape({
  fonts: PropTypes.shape({
    primary: PropTypes.shape({
      name: PropTypes.string,
      href: PropTypes.string,
    }),
  }),
  buttons: PropTypes.shape({
    borderRadius: PropTypes.string.isRequired,
    textTransform: PropTypes.string.isRequired,
  }),
  colors: PropTypes.shape({
    primary: PropTypes.string.isRequired,
    secondary: PropTypes.string.isRequired,
    tertiary: PropTypes.string.isRequired,
  }).isRequired,
  favicon: PropTypes.shape({
    path: PropTypes.string,
  }),
  logo: PropTypes.shape({
    alt: PropTypes.string,
    link: PropTypes.shape({
      href: PropTypes.string.isRequired,
      target: PropTypes.string,
    }),
    path: PropTypes.string.isRequired,
    title: PropTypes.string,
    mediumId: PropTypes.string,
  }),
});

const CompanyShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
});

const SiteShape = PropTypes.shape({
  defaultLogo: PropTypes.shape({
    alt: PropTypes.string,
    link: PropTypes.shape({
      href: PropTypes.string.isRequired,
    }).isRequired,
    title: PropTypes.string,
    url: PropTypes.string.isRequired,
  }).isRequired,
  defaultFavicon: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }).isRequired,
  theme: ThemeShape.isRequired,
  socialMediaPlatforms: PropTypes.arrayOf(SocialMediaPlatformShape).isRequired,
  customCompanyNameEnabled: PropTypes.bool,
  customCompanyName: PropTypes.string,
}).isRequired;

// Initial Values
const initialValues = {
  theme: {
    fonts: {
      primary: {
        name: 'Montserrat',
        href: availableFonts.Montserrat,
      },
    },
    buttons: {
      borderRadius: '0px',
      textTransform: 'uppercase',
    },
    colors: {
      primary: '',
      secondary: '',
      tertiary: '',
    },
    logo: {
      alt: '',
      link: {
        href: '/',
        target: '_self',
      },
      mediumId: undefined,
      path: '',
      title: '',
    },
    favicon: {
      path: '',
    },
  },
  socialMediaPlatforms: [],
  customCompanyNameEnabled: false,
  customCompanyName: '',
};

// Corrected Validation Schema
const validationSchema = object().shape({
  theme: object().shape({
    fonts: object().shape({
      primary: object().shape({
        name: string(),
        href: string().url('Invalid URL format'),
      }),
    }),
    buttons: object().shape({
      textTransform: string(),
      borderRadius: string(),
    }),
    colors: object().shape({
      primary: string()
        .required('Primary color is required')
        .test('is-primary-valid', 'Primary color is not valid', validateHexCode),
      secondary: string()
        .required('Secondary color is required')
        .test('is-secondary-valid', 'Secondary color is not valid', validateHexCode),
      tertiary: string()
        .required('Tertiary color is required')
        .test('is-tertiary-valid', 'Tertiary color is not valid', validateHexCode),
    }).required(),
    favicon: object().shape({
      path: string(),
    }),
    logo: object().shape({
      alt: string(),
      mediumId: string(),
      path: string(),
      title: string(),
    }),
  }),
  customCompanyNameEnabled: boolean(),
  customCompanyName: string().when('customCompanyNameEnabled', {
    is: true,
    then: schema => schema.required('Custom company name is required when enabled'),
    otherwise: schema => schema,
  }),
});

// Simplified PropTypes using helper shapes
const propTypes = {
  tabName: PropTypes.string.isRequired,
  appliedMedia: PropTypes.object, // Simplified for brevity
  company: CompanyShape.isRequired,
  dirty: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  selectedMedium: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  setActiveSection: PropTypes.func.isRequired,
  setAppliedMedium: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setSelectedMedium: PropTypes.func.isRequired,
  setShowMediaGallery: PropTypes.func.isRequired,
  setUsedMedia: PropTypes.func.isRequired,
  showMediaGallery: PropTypes.bool.isRequired,
  site: SiteShape,
  submitted: PropTypes.bool,
  submitting: PropTypes.bool,
  submitErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }),
  ),
  values: PropTypes.object.isRequired, // Simplified
  onDismiss: PropTypes.func.isRequired,
};

// Default Props
const defaultProps = {
  appliedMedia: {},
  selectedMedium: undefined,
  submitted: undefined,
  submitting: undefined,
  submitErrors: undefined,
  site: {}, // Add a default value for site
};

// Main Component
const SiteEditForm = ({
  tabName,
  appliedMedia,
  company,
  dirty,
  isValid,
  handleSubmit,
  handleReset,
  values,
  selectedMedium,
  setActiveSection,
  setAppliedMedium,
  setFieldValue,
  setSelectedMedium,
  setShowMediaGallery,
  setUsedMedia,
  showMediaGallery,
  site,
  submitted,
  submitting,
  submitErrors,
  onDismiss,
}) => {
  const customFaviconEnabled = useFlipper(FLIPPER_FAVICON);
  const currentUser = useCurrentUser();
  const unauthorized = currentUser.cannot(MANAGE, BUILDER);

  // Add a state for custom border radius
  const [customBorderRadius, setCustomBorderRadius] = useState(
    values?.theme?.buttons?.borderRadius || '0px',
  );


  // Function to handle radius updates
  const handleCustomRadiusChange = (newRadius) => {
    setCustomBorderRadius(newRadius);
    setFieldValue('theme.buttons.borderRadius', newRadius);
  };


  const { theme } = values;
  const { buttons } = theme;

  const availableBorderRadiusValues = buttonStylesJson.map(btn => btn.style.borderRadius);

  // Initialize selectedStyle conditionally:
  const initialSelectedStyle = buttons.borderRadius
  && availableBorderRadiusValues.includes(buttons.borderRadius)
    ? buttons.borderRadius
    : 'custom';

  const [selectedStyle, setSelectedStyle] = useState(initialSelectedStyle || '0px');
  const [selectedTextTransform, setSelectedTextTransform] = useState(buttons.textTransform || 'uppercase');

  // Memoized handlers to prevent unnecessary re-creations
  const handleSelectRadius = useCallback(
    (id, style) => {
      if (id !== 'custom') {
        setSelectedStyle(style.borderRadius);
        setFieldValue('theme.buttons.borderRadius', style.borderRadius);
      } else {
        setSelectedStyle('custom');
        setFieldValue('theme.buttons.borderRadius', customBorderRadius);
      }
    },
    [setFieldValue, customBorderRadius],
  );

  const handleSelectText = useCallback((id, style) => {
    setSelectedTextTransform(id);
    setFieldValue('theme.buttons.textTransform', style.textTransform);
  }, [setFieldValue]);

  // Effect to handle applied media changes
  useEffect(() => {
    if (appliedMedia?.[SITE_BRANDING_SECTION_NAME]?.mediumId) {
      setFieldValue('theme.logo.mediumId', appliedMedia[SITE_BRANDING_SECTION_NAME].mediumId);
      setSelectedMedium();
    }
  }, [appliedMedia, setFieldValue, setSelectedMedium]);

  // Handler to reset the logo
  const handleLogoReset = useCallback(() => {
    setFieldValue('theme.logo', {
      alt: '',
      mediumId: '',
      path: '',
      title: '',
    });
    setAppliedMedium(SITE_BRANDING_SECTION_NAME, null);
  }, [setAppliedMedium, setFieldValue]);

  // Memoized default favicon URL
  const defaultFaviconUrl = useMemo(() => get(site, 'defaultFavicon.url', ''), [site]);

  // Determine if the submit button should be disabled
  const submitDisabled = useMemo(() => (
    showMediaGallery
      ? !selectedMedium
      : (submitting || !dirty || !isValid || unauthorized)
  ), [showMediaGallery, selectedMedium, submitting, dirty, isValid, unauthorized]);

  // Function to get the description text based on color
  const getDescriptionText = (color) => {
    if (color === 'primary') {
      return 'Headings, Prices & CTAs';
    } if (color === 'secondary') {
      return 'Buttons, Subheadings & Social media';
    }
    return 'Footer Background, Hours highlight';
  };


  // Memoized rendering of color pickers to avoid repetition
  const renderColorPickers = useMemo(() => (
    <div className={styles.colorPickers}>
      {['primary', 'secondary', 'tertiary'].map(color => (
        <Col xs={4} className={styles.toggleClass} key={color}>
          <FormFieldColorPicker
            data-testid="form-field-color-picker" // Make sure this attribute is applied
            description={getDescriptionText(color)}
            disabled={unauthorized}
            label={`${capitalize(color)} Color`}
            name={`theme.colors.${color}`}
            required
          />
        </Col>
      ))}
    </div>
  ), [unauthorized]);

  // Memoized rendering of font selection dropdown
  const renderFontSelect = useMemo(() => (
    <Row>
      <div className={styles.fontBox}>
        <label htmlFor="font-select">Font</label>
        <Select
          name="font-select"
          className={styles.fontSelect} // Apply the necessary styling
          onChange={(option) => {
            const selectedFont = option.value; // react-select passes option objects
            setFieldValue('theme.fonts.primary.name', selectedFont);
            setFieldValue('theme.fonts.primary.href', availableFonts[selectedFont]);
          }}
          value={{
            value: values.theme?.fonts?.primary.name || '',
            label: values.theme?.fonts?.primary.name || 'Select Font',
          }}
          options={Object.keys(availableFonts).map(font => ({
            value: font,
            label: font,
          }))} // Map available fonts to select options
          autoBlur
          clearable={false}
        />
      </div>
    </Row>
  ), [setFieldValue, values.theme?.fonts?.primary.name,
    availableFonts, styles.fontBox, styles.fontSelect]);

  return (
    <form onSubmit={handleSubmit} onReset={handleReset} className={styles.container}>
      {/* Unauthorized Alert */}
      {unauthorized && (
        <Alert context="info">
          You do not have permission to edit site branding, please contact support
        </Alert>
      )}

      {/* Success Alert */}
      {submitted && !dirty && (
        <Alert context="success">
          Site branding successfully saved
        </Alert>
      )}

      {/* Error Alert */}
      {!submitting && submitErrors?.length > 0 && (
        <Alert context="danger">
          {submitErrors.map(({ code, message }) => (
            <div key={code}>{message}</div>
          ))}
        </Alert>
      )}

      <div className={styles.formContent}>
        {/* Logo Tab */}
        {tabName === 'logo' && (
          <>
            <Row className={styles.customCompany}>
              <Col xs={12} className={styles.toggleClass}>
                <FormFieldToggle
                  description="This will override the company name as set in your management software"
                  inline
                  label="Allow Custom Company Name"
                  type="checkbox"
                  name="customCompanyNameEnabled"
                />
                {get(values, 'customCompanyNameEnabled') && (
                  <FormFieldText
                    label="Company Name"
                    name="customCompanyName"
                    placeholder={company.name}
                    tooltip="This will update the name of your company in the footer of your website and on the privacy and terms pages."
                  />
                )}
              </Col>
            </Row>
            {customFaviconEnabled && (
              <Row>
                <Col xs={12}>
                  <FormFieldFavicon
                    description="This icon will display in browser tabs and bookmarks next to the page's name"
                    disabled={unauthorized}
                    label="Favicon"
                    name="theme.favicon.path"
                    placeholder={defaultFaviconUrl}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <FormFieldMediumUploadRow
                activeSection={SITE_BRANDING_SECTION_NAME}
                description="This image will display in the header logo widget. Horizontal logos that don’t contain extra white space around the edges work best."
                emptyMessage="Once a site logo is chosen you will be able to edit its alt and title here."
                mediumId={values.theme.logo.mediumId}
                handleReset={handleLogoReset}
                setActiveSection={setActiveSection}
                setShowMediaGallery={setShowMediaGallery}
                setFieldValue={setFieldValue}
                setUsedMedia={setUsedMedia}
                altField="theme.logo.alt"
                mediumField="theme.logo.mediumId"
                titleField="theme.logo.title"
                title="Logo"
                values={values}
              />
            </Row>
          </>
        )}

        {/* Site Tab */}
        {tabName === 'site' && (
          <div>
            {renderColorPickers}
            {renderFontSelect}
            <Row>
              <div>
                <span className={styles.buttonSelectText}>Button Shape</span>
                <div className={styles.buttonStyles}>
                  {buttonStylesJson.map(({ id, label, style }) => (
                      <div
                        key={id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          handleSelectRadius(id, style);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { handleSelectRadius(id, style); }
                        }}
                        style={{
                          ...style,
                          boxShadow: selectedStyle === style.borderRadius ? '0 0 0 6px rgba(0, 0, 0, 0.1)' : 'none',
                          border: selectedStyle === style.borderRadius ? '2px solid black' : '1px solid gray',
                          cursor: 'pointer',
                        }}
                        aria-label={`Select ${label}`}
                      />
                  ))}
                </div>
                <div className={styles.buttonStyles}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectRadius('custom', { borderRadius: customBorderRadius })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSelectRadius('custom', { borderRadius: customBorderRadius });
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '80px',
                      height: '33px',
                      padding: '8px 16px',
                      borderRadius: customBorderRadius, // Dynamically updates
                      cursor: 'pointer',
                      marginRight: '8px',
                      boxShadow: selectedStyle === 'custom' ? '0 0 0 4px rgba(0, 0, 0, 0.2)' : 'none',
                      border: selectedStyle === 'custom' ? '2px solid #000' : '1px solid gray',
                    }}
                    aria-label="Select Custom"
                  >
                    Custom
                  </div>
                </div>
              </div>
            </Row>

            {/* Show Custom Radius Input when 'Custom' is selected */}
            {selectedStyle === 'custom' && (
              <Row>
                <Col xs={12}>
                  <CustomRadiusInput
                    className={styles.customButtom} // Apply the necessary styling
                    value={customBorderRadius} // Pass the state
                    onChange={handleCustomRadiusChange} // Handle changes
                  />
                </Col>
              </Row>
            )}
            <Row>
              <div>
                <span className={styles.buttonSelectText}>Button Style</span>
                <div className={styles.buttonStylesText}>
                  {textTransformOptions.map(({ id, label, style }) => (
                    <div
                      key={id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectText(id, style)}
                      className={styles.fontSelectButton}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSelectText(id, style);
                        }
                      }}
                      style={{
                        background: selectedTextTransform === id ? '#E6E6E6' : 'transparent',
                        cursor: 'pointer',
                        ...style,
                      }}
                      aria-label={`Select ${label}`}
                    >
                      <span className={styles.fontSelectText}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Row>
          </div>
        )}

        {/* Social Media Tab */}
        {tabName === 'socialMedia' && (
          <SocialMediaIconForm
            limit={5}
            setFieldValue={setFieldValue}
            values={values}
            iconColor={values.theme.colors.secondary}
          />
        )}
      </div>

      {/* Footer with Close and Submit Buttons */}
      <div className={styles.footer}>
        <Button
          context="neutral"
          onClick={onDismiss}
          size="sm"
          className={styles.closeButton}
        >
          Close
        </Button>
        <FormSubmitButton
          disabled={submitDisabled}
          submitting={submitting}
          size="sm"
          className={styles.submitButton}
        />
      </div>
    </form>
  );
};

// Assign PropTypes and DefaultProps
SiteEditForm.propTypes = propTypes;
SiteEditForm.defaultProps = defaultProps;

// GraphQL Fragments
SiteEditForm.fragments = {
  company: gql`
    fragment SiteEditFormCompany on Company {
      name
    }
  `,
  medium: gql`
    fragment SiteEditFormMedium on Medium {
      id
      defaultMetadata {
        altText
        title
      }
    }
  `,
  site: gql`
    fragment SiteEditFormSite on Site {
      defaultLogo {
        alt
        link {
          href
        }
        title
        url
      }
      defaultFavicon {
        url
      }
      theme {
        fonts {
          primary {
            name
            href
          }
        }
        buttons {
          borderRadius
          textTransform
        }
        colors {
          primary
          secondary
          tertiary
        }
        favicon {
          path
        }
        logo {
          alt
          mediumId
          medium {
            id
            defaultMetadata {
              altText
              title
            }
          }
          path
          title
        }
      }
      socialMediaPlatforms {
        provider
        enabled
        link
      }
      customCompanyNameEnabled
      customCompanyName
    }
  `,
};
// SiteEditForm.js

// ... existing imports and code


// Export the unwrapped component and validationSchema for testing
export { SiteEditForm, validationSchema };

export default withForm({
  enableReinitialize: true,
  mapPropsToValues: props => formatInitialValues(
    initialValues,
    props.site?.theme?.logo?.medium
      ? {
        ...props.site,
        theme: {
          ...props.site.theme,
          logo: {
            ...props.site.theme.logo,
            title: props.site.theme.logo.medium.defaultMetadata.title || '',
            alt: props.site.theme.logo.medium.defaultMetadata.altText || '',
          },
        },
      }
      : props.site,
    props.site?.socialMediaPlatforms
      ? {
        ...props.site,
        socialMediaPlatforms: props.site.socialMediaPlatforms.reduce((acc, platform) => ({
          ...acc,
          [platform.provider]: {
            link: platform.link,
            enabled: platform.enabled,
          },
        }), {}),
      }
      : props.site,
  ),
  validationSchema,
  handleSubmit: (updatedSite, formikBag) => formikBag.props.onSubmit(updatedSite),
})(SiteEditForm);
